import React from 'react';
import { useField } from 'formik';
import { HStack, VStack } from '@adc/parallax-component-library';
import TextInputWithError from './TextInputWithError';
import SearchableDropdown, {
  SearchableDropdownItem,
} from '../../searchableDropdown/SearchableDropdown';
import i18n from '../../../utilities/i18n';

export const frequencyUnitValues = ['minutes', 'hours', 'days'] as const;

export type FrequencyUnit = (typeof frequencyUnitValues)[number];

type Props = {
  label: string;
  valueName: string;
  unitName: string;
  disabled?: boolean;
  testID: string;
};

/**
 * Numeric input with duration unit
 * @param {string} label The label for the input field
 * @param {string} valueName The name of the value field in the Formik config
 * @param {string} unitName The name of the unit field in the Formik config
 * @param {boolean} disabled Whether the input is disabled
 * @param {string} testID The test ID for the component
 * @returns The DurationInputWithUnit component
 */
const DurationInputWithUnit = ({
  label,
  valueName,
  unitName,
  disabled = false,
  testID,
}: Props) => {
  const [unitField, , unitHelpers] = useField<FrequencyUnit>(unitName);

  const frequencyUnitItems: SearchableDropdownItem[] = [
    {
      key: 'minutes',
      value: i18n.t('Common.unitMinutes'),
    },
    {
      key: 'hours',
      value: i18n.t('Common.unitHours'),
    },
    {
      key: 'days',
      value: i18n.t('Common.unitDays'),
    },
  ];

  const selectedItem = frequencyUnitItems.find(
    (item) => item.key === unitField.value,
  );

  return (
    <HStack gap="$1" alignItems="flex-start">
      <VStack flex={1}>
        <TextInputWithError
          label={label}
          aria-label={label}
          name={valueName}
          disabled={disabled}
          testID={`${testID}-value`}
          inputMode="numeric"
          onSubmitEditing={(e) => {
            e.stopPropagation();
          }}
        />
      </VStack>
      {/* offset by body-small line height (22) and gap between label and dropdown (8) so that the dropdown aligns with the input field */}
      <VStack flex={2} paddingTop={30}>
        <SearchableDropdown
          label=""
          aria-label="Unit"
          items={frequencyUnitItems}
          selectedItem={selectedItem}
          onChange={(item) => {
            unitHelpers.setValue(item.key as FrequencyUnit);
            unitHelpers.setTouched(true, true);
          }}
          disabled={disabled}
          testID={`${testID}-unit`}
        />
      </VStack>
    </HStack>
  );
};

export default DurationInputWithUnit;
